import { useCallback, useEffect, useRef } from 'react';
import type {
  KeyboardEvent as ReactKeyboardEvent,
  MouseEvent as ReactMouseEvent,
  RefObject,
} from 'react';

/**
 * 모달 다이얼로그의 키보드 계약 — 포커스 가둠 · 되돌리기 · ESC · 배경 스크롤 잠금.
 *
 * `role="dialog" aria-modal="true"` 는 낭독 범위만 좁힐 뿐 키보드 포커스를 가두지 않는다.
 * 마크업만 단 다이얼로그는 Tab 몇 번이면 배경 페이지로 새어나가고, 돌아올 방법이 없다.
 *
 * 원본은 `shared/ui/atoms/Modal` 안에 있던 구현이다. 도메인 톤이 달라 공용 Modal 을 쓸 수 없는
 * 다이얼로그(동아리 16-bit Lounge 계열 등)가 같은 계약을 복제하지 않도록 훅으로 분리했다 —
 * Modal 자신도 이 훅을 쓰므로 구현은 하나뿐이다.
 *
 * 호출측 계약:
 * - `overlayRef` → 전면 백드롭(= role="dialog" 를 얹는 요소). ESC 최상단 판정 마커를 훅이 직접 단다.
 * - `panelRef` → 실제 다이얼로그 상자. 포커스 가둠 범위이자 포커스 가능한 자식이 없을 때의
 *   착지점이므로 `tabIndex={-1}` 이 필요하다.
 * - `onKeyDown` 을 overlay 에, `onOverlayClick` 을 overlay 의 onClick 에 연결한다.
 */

const FOCUSABLE_SELECTOR =
  ':is(a[href], button, textarea, input, select, [tabindex]):not([disabled]):not([tabindex="-1"])';

export interface UseModalA11yOptions {
  /**
   * 오버레이가 DOM 에 붙어 있는 동안 true (기본 true). 스크롤 잠금의 수명을 정한다 —
   * 닫힘 애니메이션 중에도 유지해야 스크롤바가 애니메이션 도중 되돌아오지 않는다.
   */
  visible?: boolean;
  /** 닫힘 애니메이션 중이면 true (기본 false). 이 구간에는 포커스 이동·ESC 를 끈다. */
  closing?: boolean;
  /** false 면 ESC·백드롭 클릭으로 닫을 수 없다 (기본 true). */
  closable?: boolean;
}

export interface UseModalA11yResult<P extends HTMLElement> {
  overlayRef: RefObject<HTMLDivElement | null>;
  panelRef: RefObject<P | null>;
  onKeyDown: (event: ReactKeyboardEvent) => void;
  onOverlayClick: (event: ReactMouseEvent) => void;
}

// 여러 모달이 겹쳤을 때 마지막 하나가 닫힐 때만 body 잠금을 푼다.
let openModalCount = 0;

export function useModalA11y<P extends HTMLElement = HTMLElement>(
  onClose: () => void,
  { visible = true, closing = false, closable = true }: UseModalA11yOptions = {}
): UseModalA11yResult<P> {
  const overlayRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<P>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  // 콜백은 ref 로 추적 — 호출측 인라인 화살표가 매 렌더 리스너를 재구독하지 않게 한다.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  const active = visible && !closing;

  // 배경 스크롤 잠금 + data-modal-open (어시스턴트 위젯·다이나믹 아일랜드가 이 플래그를 읽는다).
  useEffect(() => {
    if (!visible) return;

    openModalCount++;
    document.body.style.overflow = 'hidden';
    document.body.dataset.modalOpen = 'true';

    return () => {
      openModalCount--;
      if (openModalCount === 0) {
        document.body.style.overflow = '';
        delete document.body.dataset.modalOpen;
      }
    };
  }, [visible]);

  // ESC 최상단 판정용 마커. JSX 에서 빠뜨리면 그 모달만 조용히 ESC 를 잃으므로 훅이 직접 단다.
  useEffect(() => {
    const overlay = overlayRef.current;
    if (!overlay) return;
    overlay.dataset.modalOverlay = '';
    return () => {
      delete overlay.dataset.modalOverlay;
    };
  }, [visible]);

  // 열릴 때 포커스를 안으로, 닫힐 때 열기 전 자리로.
  useEffect(() => {
    if (!active) return;

    previousFocusRef.current = document.activeElement as HTMLElement | null;

    const raf = requestAnimationFrame(() => {
      const panel = panelRef.current;
      if (!panel) return;
      const firstFocusable = panel.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
      if (firstFocusable) firstFocusable.focus();
      else panel.focus();
    });

    return () => {
      cancelAnimationFrame(raf);
      previousFocusRef.current?.focus();
    };
  }, [active]);

  // ESC는 document 레벨에서 처리 — overlay onKeyDown만으로는 포커스된 요소가
  // unmount될 때(예: 반려 폼 취소 → 버튼 제거) 포커스가 body로 떨어져 ESC가 죽는다
  // (브라우저는 요소 제거 시 blur/focusout을 발화하지 않음). 중첩 모달은 최상단만 닫는다.
  useEffect(() => {
    if (!active || !closable) return;

    const onDocKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      const overlays = document.querySelectorAll('[data-modal-overlay]');
      if (overlays[overlays.length - 1] !== overlayRef.current) return;
      onCloseRef.current();
    };

    document.addEventListener('keydown', onDocKeyDown);
    return () => document.removeEventListener('keydown', onDocKeyDown);
  }, [active, closable]);

  const onKeyDown = useCallback((event: ReactKeyboardEvent) => {
    if (event.key !== 'Tab') return;

    const panel = panelRef.current;
    if (!panel) return;

    const focusable = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey) {
      if (document.activeElement === first && last) {
        event.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last && first) {
        event.preventDefault();
        first.focus();
      }
    }
  }, []);

  const onOverlayClick = useCallback(
    (event: ReactMouseEvent) => {
      if (event.target === overlayRef.current && closable) onCloseRef.current();
    },
    [closable]
  );

  return { overlayRef, panelRef, onKeyDown, onOverlayClick };
}
